import type { CreditOption, Plan } from '@/types';

export interface PricingPlanSelection {
  id: string;
  name: string;
  price?: number;
  seats?: number;
  credits?: number;
  credit_addon_price_id?: string;
  credit_addon_credits?: number;
  accept_early_conversion?: boolean;
}

/** Seat quantity applies only to the base. Extra credits belong to the org. */
export function quotePricingV4(
  plan: Plan,
  interval: 'month' | 'year',
  members: number,
  addon?: CreditOption
) {
  const seats = Math.max(1, Math.ceil(members));
  const base = interval === 'year' ? plan.yearly_price : plan.monthly_price;
  const addonCents = addon
    ? interval === 'year'
      ? addon.yearly_price_cents
      : addon.monthly_price_cents
    : 0;
  const addonPriceId = addon
    ? interval === 'year'
      ? addon.yearly_price_id
      : addon.monthly_price_id
    : undefined;
  const credits = seats * (plan.credits_per_seat ?? 0) + (addon?.credits ?? 0);
  const total = base ? base.price * seats + addonCents / 100 : null;
  return {
    seats,
    credits,
    total,
    monthlyEquivalent:
      total == null ? null : interval === 'year' ? total / 12 : total,
    priceId: base?.id,
    addonPriceId,
    available: Boolean(base?.id && (!addon || addonPriceId)),
  };
}

/**
 * The plan a workspace holds at the AppSumo price (the AppSumo upgrade
 * campaign), as its card prices it; null for every other plan, and in the
 * yearly view, since the campaign is monthly only. The API sends the real
 * monthly total (seats and any add-on in), the subscription's seats and the
 * regular seat price; a seat is the total less the add-on held, over the
 * seats.
 */
export function appSumoPlanQuote(
  listPlan: Plan,
  current: Plan | null | undefined,
  interval: 'month' | 'year',
  addonMonthly = 0
) {
  if (!current?.appsumo_price || interval !== 'month') return null;
  const total = current.monthly_price?.price;
  const seats = current.seats;
  if (!total || total <= 0 || !seats || seats < 1) return null;
  const perSeatMonthly =
    Math.round(((total - addonMonthly) / seats) * 100) / 100;
  if (!(perSeatMonthly > 0)) return null;
  const listCents = current.list_price_per_seat_cents;
  return {
    seats,
    total,
    perSeatMonthly,
    listPerSeatMonthly:
      listCents != null && listCents > 0
        ? listCents / 100
        : (listPlan.monthly_price?.price ?? null),
    /** No credits of its own: only what the seats and the add-on bring. */
    creditsPerSeat: current.credits_per_seat ?? 0,
  };
}

/** Classes that ease a changed number up into place; key the element by it. */
export const TICK_IN =
  'inline-block motion-safe:animate-in fade-in-0 slide-in-from-bottom-1 duration-200';

/** The disabled "You're on this plan" outline button, not faded out. */
export const OWNED_BUTTON = 'disabled:opacity-100';

export const pricingMoney = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    // Whole dollars as the marketing site writes them: $89, not $89.00.
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
